import React, { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { API_ENDPOINTS } from '../../config';
import { getBearerAuthHeaders } from '../../utils/authFetchHeaders';
import { fmtCurrency, fmtPct, sentimentClass } from './clientReportingFormat';
import { HoverTip, useHoverTip } from './clientReportingHoverTip';
import ClientReportingQuoteTip from './ClientReportingQuoteTip';
import { formatQuotePrice } from './clientReportingQuoteDetails';
import { quotesBySymbolFromResponse } from './clientReportingSidebarQuotes';
import { useVisibilityAwareQuotePoll } from './useClientReportingSidebarQuotes';

export const CLIENT_REPORTING_STOCKS = [
  { symbol: 'NVDA', name: 'NVIDIA Corporation', logo: '/stocks/nvda.png' },
  { symbol: 'AAPL', name: 'Apple Inc.', logo: '/stocks/aapl.png' },
  { symbol: 'TSLA', name: 'Tesla, Inc.', logo: '/stocks/tsla.png' },
  { symbol: 'AMZN', name: 'Amazon.com, Inc.', logo: '/stocks/amzn.png' },
  { symbol: 'PLTR', name: 'Palantir Technologies', logo: '/stocks/pltr.png' },
];

function formatQuoteChange(quote) {
  const pct = Number(quote?.percentChange);
  const net = Number(quote?.netChange);
  if (!Number.isFinite(pct)) return { pctLabel: '—', netLabel: '—', tone: '' };
  return {
    pctLabel: fmtPct(pct),
    // Not `compact` — rounding to whole dollars turned a $1.21 move into a
    // bare "+$1", which is exactly the figure clients could not interpret.
    netLabel: Number.isFinite(net) ? fmtCurrency(net, { signed: true }) : '—',
    tone: sentimentClass(pct),
  };
}

export default function ClientReportingStocks({
  quotesBySymbol: quotesBySymbolProp,
  loading: loadingProp,
  warning: warningProp,
}) {
  const controlled = quotesBySymbolProp !== undefined;
  const [quotesBySymbol, setQuotesBySymbol] = useState({});
  const [loading, setLoading] = useState(!controlled);
  const [warning, setWarning] = useState('');

  const loadQuotes = useCallback(async () => {
    if (controlled) return;
    setLoading(true);
    setWarning('');
    try {
      const symbols = CLIENT_REPORTING_STOCKS.map((stock) => stock.symbol).join(',');
      const res = await fetch(API_ENDPOINTS.stockQuotes({ symbols }), {
        headers: getBearerAuthHeaders(),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.success) throw new Error(data.error || 'Failed to load stock quotes');
      setQuotesBySymbol(quotesBySymbolFromResponse(data.markets));
    } catch (err) {
      console.error('[client-reporting-stocks]', err);
      setQuotesBySymbol({});
      setWarning(err.message || 'Stock quotes unavailable.');
    } finally {
      setLoading(false);
    }
  }, [controlled]);

  useVisibilityAwareQuotePoll(loadQuotes, !controlled);

  const resolvedQuotes = controlled ? quotesBySymbolProp : quotesBySymbol;
  const resolvedLoading = controlled ? Boolean(loadingProp) : loading;
  const resolvedWarning = controlled ? warningProp : warning;
  const hasQuoteData = CLIENT_REPORTING_STOCKS.some((stock) => resolvedQuotes[stock.symbol]);

  const tip = useHoverTip({ width: 268, height: 260 });
  const navigate = useNavigate();
  const activeStock = CLIENT_REPORTING_STOCKS.find((stock) => stock.symbol === tip.activeKey);

  return (
    <section className="vd-panel vd-ml-widget vd-ml-stocks">
      <div className="vd-ml-widget-header">
        <h3>Stocks</h3>
      </div>
      {/* These tickers are a fixed watchlist, not the client's positions, and
          the figures are the stock's own price move — clients read an unlabelled
          "+$23" beside their portfolio as money they made. */}
      <p className="vd-ml-widget-caption">
        Market watchlist — not your holdings. Prices are per share and delayed;
        the change is today&apos;s move since the previous close.
        {' '}
        <strong>Hover a row for the full quote.</strong>
      </p>

      <div className={`vd-ml-market-list${resolvedLoading ? ' vd-ml-widget-loading' : ''}`}>
        {CLIENT_REPORTING_STOCKS.map((stock) => {
          const quote = resolvedQuotes[stock.symbol];
          const change = formatQuoteChange(quote);
          const priceLabel = formatQuotePrice(quote?.lastPrice ?? quote?.close);
          return (
            /* A button so the full quote is reachable by keyboard, not by
               hover alone. Every figure stays on the row either way — the
               hover box adds detail, it never gates what is already shown. */
            <button
              type="button"
              className={`vd-ml-market-row vd-ml-market-row-btn${tip.activeKey === stock.symbol ? ' active' : ''}`}
              key={stock.symbol}
              {...tip.hoverProps(stock.symbol)}
              onClick={() => navigate(`/symbol/${stock.symbol}`)}
              aria-label={`${stock.name} (${stock.symbol}). Price ${priceLabel} per share. `
                + `Change since previous close ${change.netLabel}, ${change.pctLabel}. `
                + 'Opens the symbol page.'}
            >
              <div className={`vd-ml-stock-icon vd-ml-stock-icon--${stock.symbol.toLowerCase()}`}>
                <img src={stock.logo} alt="" />
              </div>
              <div className="vd-ml-market-copy">
                <div className="vd-ml-market-name">{stock.name}</div>
                <div className="vd-ml-market-symbol">{stock.symbol}</div>
              </div>
              <div className="vd-ml-market-change">
                <span className="vd-ml-market-price">{priceLabel}</span>
                <span className="vd-ml-market-delta">
                  <span className={`vd-ml-market-pill ${change.tone}`}>{change.pctLabel}</span>
                  <span className={`vd-ml-market-net ${change.tone}`}>{change.netLabel}</span>
                </span>
              </div>
            </button>
          );
        })}

        {!resolvedLoading && !hasQuoteData && resolvedWarning ? (
          <p className="vd-ml-widget-empty">{resolvedWarning}</p>
        ) : null}
        {!resolvedLoading && !hasQuoteData && !resolvedWarning ? (
          <p className="vd-ml-widget-empty">Stock quotes unavailable. Restart the API server if you recently updated.</p>
        ) : null}
      </div>

      <HoverTip position={tip.position} width={268} className="vd-ml-quote-tip">
        {activeStock ? (
          <ClientReportingQuoteTip
            title={activeStock.name}
            subtitle={activeStock.symbol}
            quote={resolvedQuotes[activeStock.symbol]}
            kind="stock"
          />
        ) : null}
      </HoverTip>
    </section>
  );
}
